import { ReferralLink } from "types/affiliation.types";
import Table from "common/Table/Table";
import React from "react";
import { ColumnsType } from "antd/es/table";
import {
  FROM_LAPTOP_COLUMN,
  FROM_TABLET_COLUMN,
} from "common/NewLook/responsiveColumns.ts";
import Text from "common/Typography/Text";
import {
  copyUrlToClipboard,
  getReferralUrl,
} from "features/affiliation/common/utils";
import ReferralLinkActions from "features/affiliation/ReferralLinks/ReferralLinkActions";
import {
  ActionsWrapper,
  BlueCopyOutlined,
  CopyText,
} from "features/affiliation/common/styles";
import useMedia from "utils/hooks/useMedia";
import { SMALL_SCREEN } from "common/constants";
import {
  AFFILIATION_ARTICLE_ID,
  USD_PRECISION,
} from "features/affiliation/common/constants";
import {
  sortByNumberProperty,
  sortByStringProperty,
} from "@shared/utils/general";
import { formatDate } from "@shared/utils/date";
import { toFixed } from "@shared/utils/numbers";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import { AnalyticsPageName, LanguageNameSpaces } from "@shared/types";
import ReferralEmptyState from "features/affiliation/common/ReferralEmptyState";
import { AffiliationTabs } from "features/affiliation/common/types";
import { checkSearch } from "@shared/utils/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import styled, { useTheme } from "styled-components";
import { CopyValue } from "features/affiliation/NewLook/parts";
import {
  formatUsd,
  NUMBER_COLUMN,
} from "features/affiliation/NewLook/newLookUtils";
import {
  CellFigure,
  CellText,
  DateCell,
  Stack,
} from "features/affiliation/NewLook/TablePanel";

interface ReferralLinksTableProps {
  referralLinks?: ReferralLink[];
  isLoading: boolean;
  searchFilter?: string;
}

const ReferralLinksTable = ({
  referralLinks,
  isLoading,
  searchFilter,
}: ReferralLinksTableProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.AFFILIATION);
  const { isNewLook } = useTheme();
  const isSmallScreen = useMedia(
    [`(max-width: ${SMALL_SCREEN})`],
    [true],
    false
  );

  const filteredReferralLinks = referralLinks?.filter((referralLink) =>
    checkSearch(referralLink.referral_code, searchFilter)
  );

  const columns: ColumnsType<ReferralLink> = [
    {
      title: t("links.created_date"),
      dataIndex: "time",
      showSorterTooltip: false,
      defaultSortOrder: "descend",
      sorter: (a, b) => sortByNumberProperty(a, b, "time"),
      render: (time) => <Text>{formatDate(time, !isSmallScreen, true)}</Text>,
    },
    {
      title: t("links.description"),
      dataIndex: "description",
      showSorterTooltip: false,
      sorter: (a, b) => sortByStringProperty(a, b, "description"),
      render: (description) => (
        <Text style={{ minWidth: 150, whiteSpace: "break-spaces" }}>
          {description}
        </Text>
      ),
    },
    {
      title: (
        <div style={{ display: "flex" }}>
          {t("links.referral_link")}
          <HelperTooltip
            analyticsPageName={AnalyticsPageName.AFFILIATION_REFERRAL_LINKS}
            analyticsFieldName="Referral Link"
            description={t("links.referral_link_tooltip")}
            articleId={AFFILIATION_ARTICLE_ID}
          />
        </div>
      ),
      dataIndex: "referral_code",
      showSorterTooltip: false,
      sorter: (a, b) => sortByStringProperty(a, b, "referral_code"),
      render: (referralCode) => {
        const url = getReferralUrl(referralCode);
        if (isNewLook)
          return (
            <CopyValue
              onClick={() => copyUrlToClipboard(url)}
              isLink={true}
              maxWidth={isSmallScreen ? 220 : 320}
              title={url}
            >
              {url}
            </CopyValue>
          );
        return (
          <CopyText onClick={() => copyUrlToClipboard(url)}>
            <BlueCopyOutlined />
            <Text>{url}</Text>
          </CopyText>
        );
      },
    },
    {
      title: t("links.clicks"),
      dataIndex: "clicks",
      showSorterTooltip: false,
      sorter: (a, b) => sortByNumberProperty(a, b, "clicks"),
      render: (clicks) => <Text>{clicks}</Text>,
    },
    {
      title: t("links.registered"),
      dataIndex: "registered_users",
      showSorterTooltip: false,
      sorter: (a, b) => sortByNumberProperty(a, b, "registered_users"),
      render: (registeredUsers) => <Text>{registeredUsers}</Text>,
    },
    {
      title: t("links.paid"),
      dataIndex: "paid_users",
      showSorterTooltip: false,
      sorter: (a, b) => sortByNumberProperty(a, b, "paid_users"),
      render: (paidUsers) => <Text>{paidUsers}</Text>,
    },
    {
      title: t("links.earned"),
      dataIndex: "commission",
      showSorterTooltip: false,
      sorter: (a, b) => sortByNumberProperty(a, b, "commission"),
      render: (commission) =>
        isNewLook ? (
          formatUsd(commission)
        ) : (
          <Text>${toFixed(commission, USD_PRECISION)}</Text>
        ),
    },
    {
      title: "",
      dataIndex: "referral_code",
      key: "action",
      width: 40,
      render: (_, referralLink) => (
        <ActionsWrapper onClick={(e) => e.stopPropagation()}>
          <ReferralLinkActions referralLinkData={referralLink} />
        </ActionsWrapper>
      ),
    },
  ];

  // New look: the link's name over its URL, the funnel figures right-aligned.
  const newLookColumns: ColumnsType<ReferralLink> = [
    {
      title: (
        <TitleWithTooltip>
          {t("links.referral_link")}
          <HelperTooltip
            analyticsPageName={AnalyticsPageName.AFFILIATION_REFERRAL_LINKS}
            analyticsFieldName="Referral Link"
            description={t("links.referral_link_tooltip")}
            articleId={AFFILIATION_ARTICLE_ID}
          />
        </TitleWithTooltip>
      ),
      dataIndex: "description",
      showSorterTooltip: false,
      sorter: (a, b) => sortByStringProperty(a, b, "description"),
      render: (description, referralLink) => {
        const url = getReferralUrl(referralLink.referral_code);
        return (
          <Stack>
            <CellText title={description}>
              {description || referralLink.referral_code}
            </CellText>
            <CopyValue
              onClick={() => copyUrlToClipboard(url)}
              isLink={true}
              small={true}
              maxWidth={isSmallScreen ? 200 : 300}
              title={url}
            >
              {url}
            </CopyValue>
          </Stack>
        );
      },
    },
    {
      title: t("links.created_date"),
      dataIndex: "time",
      showSorterTooltip: false,
      defaultSortOrder: "descend",
      ...FROM_LAPTOP_COLUMN,
      sorter: (a, b) => sortByNumberProperty(a, b, "time"),
      render: (time) => <DateCell time={time} />,
    },
    ...(
      [
        ["clicks", t("links.clicks")],
        ["registered_users", t("links.registered")],
        ["paid_users", t("links.paid")],
      ] as const
    ).map(([key, title]) => ({
      ...NUMBER_COLUMN,
      ...(key === "clicks" && FROM_LAPTOP_COLUMN),
      ...(key === "paid_users" && FROM_TABLET_COLUMN),
      title,
      dataIndex: key,
      showSorterTooltip: false,
      sorter: (a: ReferralLink, b: ReferralLink) =>
        sortByNumberProperty(a, b, key),
      render: (value: number) => (
        <CellText>{value.toLocaleString("en-US")}</CellText>
      ),
    })),
    {
      ...NUMBER_COLUMN,
      title: t("links.earned"),
      dataIndex: "commission",
      showSorterTooltip: false,
      sorter: (a, b) => sortByNumberProperty(a, b, "commission"),
      render: (commission) => <CellFigure>{formatUsd(commission)}</CellFigure>,
    },
    columns[columns.length - 1],
  ];

  return (
    <Table<ReferralLink>
      columns={isNewLook ? newLookColumns : columns}
      rowKey={(link) => link.referral_id}
      dataSource={filteredReferralLinks}
      loading={isLoading}
      locale={{
        emptyText: (
          <ReferralEmptyState
            tab={AffiliationTabs.REFERRAL_LINKS}
            isSearch={!!searchFilter?.length}
          />
        ),
      }}
    />
  );
};

export default ReferralLinksTable;

const TitleWithTooltip = styled.div`
  display: flex;
  align-items: center;
`;
